import { FC } from 'react'
import { createPortal } from 'react-dom'
import Markdown from 'react-markdown'
import { ReactComponent as BrandIcon } from 'src/assets/svg/camp-card.svg'
import { ReactComponent as CitationInfoIcon } from 'src/assets/svg/citation-info.svg'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/Globe.svg'
import { getAiToolIcon } from 'src/components/AiToolIcon'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { AiListeningResponseType } from 'src/services/__generated__/api'
import styles from './aiResponseModal.module.scss'

// The responses endpoint returns the raw document, which carries the sources
// each answer cited. The generated swagger type doesn't document the field yet.
export interface AiResponseCitation {
  title?: string
  url?: string
}

export type AiResponseWithCitations = AiListeningResponseType & {
  citations?: AiResponseCitation[]
}

// Only citations with a usable URL are listed/numbered.
export const usableCitations = (
  response: AiResponseWithCitations
): AiResponseCitation[] =>
  (response.citations ?? []).filter((c) => Boolean(c?.url))

// "https://www.magticom.ge/en/..." -> "magticom.ge"
export const citationDomain = (url?: string): string => {
  if (!url) return ''
  try {
    return new URL(url).hostname.replace(/^www\./i, '')
  } catch {
    return url
  }
}

interface AIResponseModalProps {
  // Pre-formatted model label; callers differ on how they present it.
  modelLabel?: string
  onClose: () => void
  response: AiResponseWithCitations
}

export const AIResponseModal: FC<AIResponseModalProps> = ({
  modelLabel,
  onClose,
  response,
}) => {
  const citations = usableCitations(response)
  const IconComponent = getAiToolIcon(response.aiTool)

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  // Responses cite their sources as inline markdown links. Render each one that
  // matches a listed citation as its "[n]" reference instead of the raw link
  // text, so the prose matches the numbered Citation Source list beside it.
  const markdownComponents = {
    a: ({ children, href }: { children?: React.ReactNode; href?: string }) => {
      const index = citations.findIndex((c) => c.url === href)
      return (
        <a
          className={index >= 0 ? styles.citationRef : styles.responseLink}
          href={href}
          rel="noopener noreferrer"
          target="_blank"
        >
          {index >= 0 ? `[${index + 1}]` : children}
        </a>
      )
    },
  }

  return createPortal(
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      {/* Without citations there's no second column, so the modal falls back to
          its original narrow, single-column layout. */}
      <div
        className={`${styles.modal} ${citations.length === 0 ? styles.modalCompact : ''}`}
      >
        <div className={styles.modalHeader}>
          <div className={styles.modalToolInfo}>
            {IconComponent && <IconComponent className={styles.aiToolIcon} />}
            <span className={styles.aiToolName}>{response.aiTool}</span>
            <span className={styles.aiToolVersion}>
              {modelLabel ?? response.model}
            </span>
          </div>
          <button className={styles.modalClose} onClick={onClose} type="button">
            &times;
          </button>
        </div>

        <div className={styles.modalPills}>
          <div
            className={`${styles.statusPill} ${response.brandMentioned ? styles.statusYes : styles.statusNo}`}
          >
            <BrandIcon className={styles.statusIcon} />
            Brand Mentioned: {response.brandMentioned ? 'Yes' : 'No'}
          </div>
          <div
            className={`${styles.statusPill} ${response.domainMentioned ? styles.statusYes : styles.statusNo}`}
          >
            <GlobeIcon className={styles.statusIcon} />
            Domain Mentioned: {response.domainMentioned ? 'Yes' : 'No'}
          </div>
        </div>

        {/* Main content: response + citation list, framed by top/bottom
            hairlines and split by a vertical rule. */}
        <div
          className={`${styles.modalMain} ${citations.length === 0 ? styles.modalMainCompact : ''}`}
        >
          <div className={styles.modalLeft}>
            <h3 className={styles.sectionTitle}>Full Response</h3>
            <div className={styles.modalResponseWrapper}>
              <div className={styles.modalResponseText}>
                <Markdown components={markdownComponents}>
                  {response.fullResponse}
                </Markdown>
              </div>
            </div>
            {citations.length > 0 && (
              <div className={styles.citationNotice}>
                <CitationInfoIcon className={styles.citationNoticeIcon} />
                <span className={styles.citationNoticeText}>
                  Citations link to the source used by AI to generate this
                  response. click on any number to view that source
                </span>
              </div>
            )}
          </div>

          {citations.length > 0 && (
            <>
              <div className={styles.modalSplit} />

              <div className={styles.modalRight}>
                <h4 className={styles.citationHeader}>
                  Citation Source ({citations.length})
                </h4>
                <div className={styles.citationList}>
                  {citations.map((citation, i) => (
                    <a
                      className={styles.citationItem}
                      href={citation.url}
                      key={citation.url ?? i}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span className={styles.citationIndex}>[{i + 1}]</span>
                      <span className={styles.citationInfo}>
                        {/* Shows the custom white tooltip only when the title
                            is actually clamped. */}
                        <TruncatedTooltip
                          className={styles.citationTitle}
                          maxLines={2}
                          text={citation.title || citationDomain(citation.url)}
                        />
                        <span className={styles.citationDomain}>
                          {citationDomain(citation.url)}
                        </span>
                      </span>
                      <svg
                        className={styles.citationLinkIcon}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                        />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <h3 className={styles.sectionTitle}>Mentioned competitors</h3>
          <div className={styles.competitorsList}>
            {!response.competitors || response.competitors.length === 0 ? (
              <span className={styles.naText}>N/A</span>
            ) : (
              response.competitors.map((competitor, i) => (
                <span className={styles.competitorPill} key={i}>
                  # {competitor}
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
